export default function ketoCalculator(targetWeightKg) {
	return {
		// --- Inputs ---
		units: true, // 'imperial' or 'metric'
		gender: 'man', // 'man' or 'woman'
		age: '',
		// Height (stored internally as cm)
		heightCm: '',
		heightFt: '',
		heightIn: '',
		// Current Weight (stored internally as kg)
		currentWeightKg: '',
		currentWeightLbs: '',
		// Target Weight (stored internally as kg)
		targetWeightKg: targetWeightKg || '',
		targetWeightLbs: parseFloat(((targetWeightKg || 0) * 2.20462).toFixed(1)) || '',
		// Activity
		activityMultiplier: 1.375,
		// Goals
		desiredNetCarbsG: 20,
		desiredProteinPerKgG: 1.0,
		// Macro Percentages (editable)
		fatPercent: 70,
		carbPercent: 5,
		proteinPercent: 25,

		// --- Internal/Calculated ---
		bmr: 0,
		maintenanceTDEE: 0,
		targetCalories: 0,
		weightChangePercentage: 0,
		fatCalories: 0,
		fatGrams: 0,
		carbCalories: 0,
		carbGrams: 0,
		proteinCalories: 0,
		proteinGrams: 0,

		// --- Initialization ---
		init() {
			// Watchers for unit conversions and calculations
			this.$watch('units', () => this.handleUnitChange());
			this.$watch('heightFt', () => { if (this.units === true) this.convertImperialHeightToCm(); });
			this.$watch('heightIn', () => { if (this.units === true) this.convertImperialHeightToCm(); });
			this.$watch('currentWeightLbs', () => { if (this.units === true) this.convertImperialWeightToKg(); });
			this.$watch('targetWeightLbs', () => { if (this.units === true) this.convertImperialTargetWeightToKg(); });
			this.$watch('heightCm', () => this.updateAllCalculations());
			this.$watch('currentWeightKg', () => this.updateAllCalculations());
			this.$watch('targetWeightKg', () => this.updateAllCalculations());
			this.$watch('age', () => this.updateAllCalculations());
			this.$watch('gender', () => this.updateAllCalculations());
			this.$watch('activityMultiplier', () => this.updateAllCalculations());
			this.$watch('desiredNetCarbsG', () => this.updateAllCalculations());
			this.$watch('desiredProteinPerKgG', () => this.updateAllCalculations());

			// Initial calculation run
			this.updateAllCalculations();
		},

		// --- Unit Conversion Helpers ---
		convertImperialHeightToCm() {
			this.heightCm = ((this.heightFt || 0) * 12 + (this.heightIn || 0)) * 2.54;
		},
		convertCmToImperialHeight() {
			const totalInches = (this.heightCm || 0) / 2.54;
			this.heightFt = Math.floor(totalInches / 12);
			this.heightIn = Math.round(totalInches % 12);
		},
		convertImperialWeightToKg() {
			this.currentWeightKg = (this.currentWeightLbs || 0) / 2.20462;
		},
		convertKgToImperialWeight() {
			this.currentWeightLbs = (this.currentWeightKg || 0) * 2.20462;
		},
		convertImperialTargetWeightToKg() {
			this.targetWeightKg = (this.targetWeightLbs || 0) / 2.20462;
		},
		convertKgToImperialTargetWeight() {
			this.targetWeightLbs = (this.targetWeightKg || 0) * 2.20462;
		},
		handleUnitChange() {
			if (this.units === true) {
				this.convertCmToImperialHeight();
				this.convertKgToImperialWeight();
				this.convertKgToImperialTargetWeight();
			} else {
				// When switching back to metric, values are already stored correctly
				// Optionally re-round metric display values if needed
				this.heightCm = parseFloat((this.heightCm || 0).toFixed(1));
				this.currentWeightKg = parseFloat((this.currentWeightKg || 0).toFixed(1));
				this.targetWeightKg = parseFloat((this.targetWeightKg || 0).toFixed(1));
			}
			// Recalculate everything as units might affect rounding/display indirectly
			this.updateAllCalculations();
		},

		// --- Core Calculation Logic ---
		isValidInput() {
			return this.age > 0 && this.heightCm > 0 && this.currentWeightKg > 0 && this.targetWeightKg > 0 && this.activityMultiplier > 0 && this.desiredNetCarbsG >= 0 && this.desiredProteinPerKgG >= 0;
		},

		calculateBMR() {
			if (!this.isValidInput()) return 0;
			let bmrCalc = 0;
			// Mifflin-St Jeor Equation
			if (this.gender === 'man') {
				bmrCalc = (10 * this.currentWeightKg) + (6.25 * this.heightCm) - (5 * this.age) + 5;
			} else { // woman
				bmrCalc = (10 * this.currentWeightKg) + (6.25 * this.heightCm) - (5 * this.age) - 161;
			}
			this.bmr = bmrCalc > 0 ? bmrCalc : 0;
			return this.bmr;
		},

		calculateMaintenanceTDEE() {
			if (!this.isValidInput()) return 0;
			this.maintenanceTDEE = this.calculateBMR() * this.activityMultiplier;
			return this.maintenanceTDEE;
		},

		calculateTargetCalories() {
			if (!this.isValidInput()) return 0;
			const tdee = this.calculateMaintenanceTDEE();
			if (tdee <= 0 || this.currentWeightKg <= 0) {
				this.weightChangePercentage = 0;
				this.targetCalories = 0;
				return 0;
			}

			this.weightChangePercentage = ((this.targetWeightKg - this.currentWeightKg) / this.currentWeightKg) * 100;

			// Apply weight change percentage to TDEE
			// Note: Removed the explicit 15%/30% cap from calculation logic here,
			// relies on UI warnings. You could add Math.max/min capping if desired.
			this.targetCalories = tdee + (tdee * (this.weightChangePercentage / 100));
			this.targetCalories = this.targetCalories > 0 ? this.targetCalories : 0; // Ensure not negative
			return this.targetCalories;
		},

		calculateInitialMacros() {
			if (this.targetCalories <= 0 || !this.isValidInput()) {
				this.resetMacroValues();
				return;
			}

			// 1. Calculate Carb Calories & Grams
			this.carbGrams = this.desiredNetCarbsG >= 0 ? this.desiredNetCarbsG : 0;
			this.carbCalories = this.carbGrams * 4;

			// 2. Calculate Protein Calories & Grams
			this.proteinGrams = (this.desiredProteinPerKgG >= 0 ? this.desiredProteinPerKgG : 0) * this.currentWeightKg;
			this.proteinCalories = this.proteinGrams * 4;

			// 3. Calculate Fat Calories & Grams (Remainder)
			this.fatCalories = this.targetCalories - this.carbCalories - this.proteinCalories;
			// Prevent negative fat calories if carbs/protein exceed target
			if (this.fatCalories < 0) {
				// Prioritize protein, then carbs, adjust fat last.
				// Or better: adjust all proportionally - simpler: just set fat to 0
				console.warn("Carb + Protein calories exceed target. Fat set to 0.");
				this.fatCalories = 0;
			}
			this.fatGrams = this.fatCalories / 9;

			// 4. Calculate initial percentages and UPDATE the percentage inputs
			if (this.targetCalories > 0) {
				this.fatPercent = parseFloat(((this.fatCalories / this.targetCalories) * 100).toFixed(1));
				this.carbPercent = parseFloat(((this.carbCalories / this.targetCalories) * 100).toFixed(1));
				// Calculate protein percentage last to ensure total is closer to 100
				this.proteinPercent = parseFloat((100 - this.fatPercent - this.carbPercent).toFixed(1));
				// Correct potential rounding issues slightly
				this.normalizePercentages();
			} else {
				this.fatPercent = 0;
				this.carbPercent = 0;
				this.proteinPercent = 0;
			}
		},

		updateMacrosFromPercentages(source) {
			if (this.targetCalories <= 0 || !this.isValidInput()) {
				this.resetMacroValues();
				return;
			}

			// Ensure percentages are numbers
			this.fatPercent = Number(this.fatPercent) || 0;
			this.carbPercent = Number(this.carbPercent) || 0;
			this.proteinPercent = Number(this.proteinPercent) || 0;

			// Balance the other two percentages
			let totalPercent = this.fatPercent + this.carbPercent + this.proteinPercent;
			let difference = 100 - totalPercent;

			// Simple balancing: Adjust the *other* two proportionally based on input
			// More robust: Adjust the one that *wasn't* just edited.
			if (difference !== 0) {
				if (source === 'fat') {
					// Adjust carb and protein proportionally? Or just one? Let's adjust carb first.
					let currentCarb = this.carbPercent;
					let currentProtein = this.proteinPercent;
					if (currentCarb + currentProtein > 0) { // Avoid division by zero
						this.carbPercent = Math.max(0, parseFloat((currentCarb + difference * (currentCarb / (currentCarb + currentProtein))).toFixed(1)));
						this.proteinPercent = Math.max(0, 100 - this.fatPercent - this.carbPercent); // Protein gets remainder
					} else if (difference > 0) { // If both were 0, split difference
						this.carbPercent = parseFloat((difference / 2).toFixed(1));
						this.proteinPercent = 100 - this.fatPercent - this.carbPercent;
					} else { // If difference negative, can't adjust non-edited ones if they are 0
						this.carbPercent = 0;
						this.proteinPercent = 100 - this.fatPercent; // Protein gets remainder
					}
				} else if (source === 'carb') {
					let currentFat = this.fatPercent;
					let currentProtein = this.proteinPercent;
					if (currentFat + currentProtein > 0) {
						this.fatPercent = Math.max(0, parseFloat((currentFat + difference * (currentFat / (currentFat + currentProtein))).toFixed(1)));
						this.proteinPercent = Math.max(0, 100 - this.fatPercent - this.carbPercent); // Protein gets remainder
					} else if (difference > 0) {
						this.fatPercent = parseFloat((difference / 2).toFixed(1));
						this.proteinPercent = 100 - this.fatPercent - this.carbPercent;
					} else {
						this.fatPercent = 0;
						this.proteinPercent = 100 - this.carbPercent; // Protein gets remainder
					}
				} else { // source === 'protein'
					let currentFat = this.fatPercent;
					let currentCarb = this.carbPercent;
					if (currentFat + currentCarb > 0) {
						this.fatPercent = Math.max(0, parseFloat((currentFat + difference * (currentFat / (currentFat + currentCarb))).toFixed(1)));
						this.carbPercent = Math.max(0, 100 - this.fatPercent - this.proteinPercent); // Carb gets remainder
					} else if (difference > 0) {
						this.fatPercent = parseFloat((difference / 2).toFixed(1));
						this.carbPercent = 100 - this.fatPercent - this.proteinPercent;
					} else {
						this.fatPercent = 0;
						this.carbPercent = 100 - this.proteinPercent; // Carb gets remainder
					}
				}
				// Final check to ensure 100%
				this.normalizePercentages();
			}


			// Recalculate calories and grams based on the *new* percentages
			this.fatCalories = this.targetCalories * (this.fatPercent / 100);
			this.carbCalories = this.targetCalories * (this.carbPercent / 100);
			this.proteinCalories = this.targetCalories * (this.proteinPercent / 100);

			this.fatGrams = this.fatCalories / 9;
			this.carbGrams = this.carbCalories / 4;
			this.proteinGrams = this.proteinCalories / 4;
		},

		normalizePercentages() {
			// Adjust slightly if rounding caused total != 100
			let total = this.fatPercent + this.carbPercent + this.proteinPercent;
			if (total !== 100 && total !== 0) {
				let diff = 100 - total;
				// Apply difference to the largest percentage (usually fat)
				this.fatPercent = parseFloat((this.fatPercent + diff).toFixed(1));
				// Ensure non-negative percentages
				this.fatPercent = Math.max(0, this.fatPercent);
				this.carbPercent = Math.max(0, this.carbPercent);
				this.proteinPercent = Math.max(0, this.proteinPercent);
				// If adjusting fat made it negative, reset and adjust another
				if (this.fatPercent < 0) {
					// This case needs more complex handling - for now, just ensure sum is close
					console.warn("Percentage normalization needed more complex logic.")
				}
			}
			// Ensure non-negative final check
			this.fatPercent = Math.max(0, this.fatPercent);
			this.carbPercent = Math.max(0, this.carbPercent);
			this.proteinPercent = Math.max(0, this.proteinPercent);
		},

		resetMacroValues() {
			this.fatCalories = 0; this.fatGrams = 0;
			this.carbCalories = 0; this.carbGrams = 0;
			this.proteinCalories = 0; this.proteinGrams = 0;
			// Maybe reset percentages too? Or keep user input? Let's keep them for now.
			// this.fatPercent = 70; this.carbPercent = 5; this.proteinPercent = 25;
		},

		updateAllCalculations() {
			// console.log("Updating all calculations...");
			if (this.isValidInput()) {
				this.calculateTargetCalories(); // This cascades BMR and TDEE
				this.calculateInitialMacros(); // Recalculate based on goals, updates percentages too
			} else {
				this.targetCalories = 0;
				this.maintenanceTDEE = 0;
				this.weightChangePercentage = 0;
				this.resetMacroValues();
			}
		},

		weightChangePercentageBiggerThan() {
			return this.weightChangePercentage > 15
		},

		weightChangePercentageSmallerThan() {
			return this.weightChangePercentage < -30
		}

		// Optional Reset Function
		// resetDefaults() {
		//     this.units = 'metric';
		//     this.gender = 'male';
		//     this.age = 30;
		//     this.heightCm = 180;
		//     this.currentWeightKg = 80;
		//     this.targetWeightKg = 75;
		//     this.activityMultiplier = 1.375;
		//     this.desiredNetCarbsG = 20;
		//     this.desiredProteinPerKgG = 1.0;
		//     this.handleUnitChange(); // Update imperial inputs if needed
		//     this.updateAllCalculations();
		// }
	}
}
